// ============================================================
// YOM Intent — scoring (plan §6, docs/intent.md §6)
//
// Pure functions. The pipeline folds a queue batch into one delta per
// profile (extractIntentBatch) and sends it to the project's profile store in
// one call; the store adds the deltas to decayed scores. Stages are evaluated
// when read (stageOf), so they age without writes.
// ============================================================
import { advertisingAllowed, advertisingBasisOf } from '../consent.js';
import { CONSENT_RULES, ruleRef } from '../consent-rules.js';
import { intentConfigOf, intentRetentionDays } from './config.js';
import { analyticsGranted } from './identifiers.js';
import { DEFAULT_HALF_LIFE_DAYS, DEFAULT_HALF_LIVES, DEFAULT_SIGNAL_EVENTS, KIND_DEFAULTS, MIN_PURCHASE_COOLDOWN_DAYS, NEEDS_CATEGORY, PURCHASE_COOLDOWN_HALF_LIVES, STAGE_SCORES, } from './kinds.js';
import { UNKNOWN_CATEGORY, gptLineage, gptTop, isSensitiveCategory, resolveGptCategory, } from './taxonomy.js';
const DAY_MS = 86_400_000;
/** The signal an event counts as, or null (not an Intent signal). */
export function signalOf(event, cfg) {
    const spec = cfg.signals[event];
    const kind = spec?.kind ?? DEFAULT_SIGNAL_EVENTS[event];
    if (!kind) {
        return null;
    }
    const cap = spec?.sessionCap ?? KIND_DEFAULTS[kind].sessionCap;
    return {
        kind,
        weight: spec?.weight ?? cfg.weights[kind] ?? KIND_DEFAULTS[kind].weight,
        ...(cap !== undefined ? { sessionCap: cap } : {}),
    };
}
/** Half-life (days) of a category: the most specific configured node, then built-ins, then `*`. */
export function halfLifeDays(cat, cfg) {
    for (const id of gptLineage(cat)) {
        const own = cfg.halfLives[String(id)];
        if (own !== undefined) {
            return own;
        }
    }
    for (const id of gptLineage(cat)) {
        const builtIn = DEFAULT_HALF_LIVES[id];
        if (builtIn !== undefined) {
            return builtIn;
        }
    }
    return cfg.halfLives['*'] ?? DEFAULT_HALF_LIFE_DAYS;
}
export function purchaseCooldownMs(hlMs) {
    return Math.max(PURCHASE_COOLDOWN_HALF_LIVES * hlMs, MIN_PURCHASE_COOLDOWN_DAYS * DAY_MS);
}
function itemsOf(ev) {
    const raw = ev.params.items;
    return Array.isArray(raw)
        ? raw.filter((i) => !!i && typeof i === 'object')
        : [];
}
/** One record (event params or an item) → GPT id: the category param, then the mapping. */
function categoryOfRecord(rec, cfg) {
    const direct = resolveGptCategory(rec[cfg.taxonomy.param]);
    if (direct !== null) {
        return direct;
    }
    const label = rec.item_category;
    if (typeof label === 'string') {
        const mapped = cfg.taxonomy.map[label.trim().toLowerCase()];
        if (mapped !== undefined) {
            return mapped;
        }
    }
    return null;
}
/**
 * Categories an event is about. Purchases: every item's category (each closes its own
 * in-market). Other events: the event's category, else the first item's; unknown = 0.
 */
export function categoriesOf(ev, cfg) {
    const items = itemsOf(ev);
    if (ev.event === 'purchase' || signalOf(ev.event, cfg)?.kind === 'purchase') {
        const cats = items.map(i => categoryOfRecord(i, cfg) ?? UNKNOWN_CATEGORY);
        if (cats.length > 0) {
            return [...new Set(cats)];
        }
    }
    const own = categoryOfRecord(ev.params, cfg);
    if (own !== null) {
        return [own];
    }
    const first = items[0] ? categoryOfRecord(items[0], cfg) : null;
    return [first ?? UNKNOWN_CATEGORY];
}
// --- Decay and stages ---
/** score(t) = score(at) × 0.5^((t − at) / halfLife). */
export function decay(score, from, to, hlMs) {
    if (score === 0 || to <= from || hlMs <= 0) {
        return score;
    }
    return score * Math.pow(0.5, (to - from) / hlMs);
}
/** The category's stage now (null = dormant: in no segment). */
export function stageOf(row, now) {
    if (row.purchased_at > 0 && now - row.purchased_at < purchaseCooldownMs(row.hl)) {
        return 'purchased';
    }
    const score = decay(row.score, row.at, now, row.hl);
    if (score < STAGE_SCORES.browsing) {
        return null;
    }
    if (score >= STAGE_SCORES.intent || (row.intent_at > 0 && now - row.intent_at < row.hl)) {
        return 'intent';
    }
    if (score >= STAGE_SCORES.considering ||
        (row.consider_at > 0 && now - row.consider_at < row.hl)) {
        return 'considering';
    }
    return 'browsing';
}
export const STAGE_RANK = {
    browsing: 1,
    considering: 2,
    intent: 3,
    purchased: 0,
};
// --- Extraction (pipeline) ---
function consentOf(ev) {
    const a = ev.consent;
    return {
        a: a === 'granted' ? 'g' : a === 'denied' ? 'd' : 'u',
        p: statesPersonalisation(ev) ? ev.personalisation === true : null,
        d: advertisingAllowed(ev),
        // GPC already cleared `marketing`; the basis is the Consent SDK's stamp (G1).
        basis: advertisingAllowed(ev) ? advertisingBasisOf(ev) : null,
        at: ev.ts,
    };
}
const CONSENT_EVENT = 'consent_update';
/**
 * Who may set a profile's consent: the browser (where the person decides), a consent receipt
 * from either side, or a server event that states advertising (a grant, or GPC). A server
 * event that only says `consent: granted` never downgrades what the person chose.
 */
export function statesConsent(ev) {
    return (ev.source === 'client' ||
        ev.event === CONSENT_EVENT ||
        ev.marketing === true ||
        ev.gpc === true);
}
/**
 * Who may state personalisation, the purpose profiles and scores rest on: only the browser and
 * consent receipts. A server event never does, even when its legacy `consent: granted` maps to
 * personalisation (consent.ts), so an order webhook can't create a profile the person refused.
 */
export function statesPersonalisation(ev) {
    return ev.source === 'client' || ev.event === CONSENT_EVENT;
}
const OPT_OUT_RULES = new Set(CONSENT_RULES.filter(r => r.mode === 'opt_out').map(ruleRef));
/**
 * A consent receipt that denies advertising as the person's action: a change or withdrawal,
 * or the first decision under an opt-out rule (advertising was on by default). A first refusal
 * under an opt-in rule never granted anything, so it isn't an opt-out.
 */
function deniesAdvertising(ev) {
    const p = ev.params;
    return (ev.event === CONSENT_EVENT &&
        p.advertising === 'denied' &&
        (p.kind !== 'new' || OPT_OUT_RULES.has(String(p.rule))));
}
function profileKeysOf(ev) {
    const keys = {};
    if (ev.visitor && !ev.privacy?.cookieless) {
        keys.v = ev.visitor;
    }
    if (ev.userId) {
        keys.u = ev.userId;
    }
    if (ev.hashed?.em) {
        keys.em = ev.hashed.em;
    }
    if (ev.hashed?.ph) {
        keys.ph = ev.hashed.ph;
        if (ev.hashed.phd) {
            keys.phd = ev.hashed.phd;
        }
    }
    return keys;
}
/** Batch-local grouping: by visitor, else user id, else a hashed id. */
function groupKey(st, k) {
    const id = k.v
        ? `v:${k.v}`
        : k.u
            ? `u:${k.u}`
            : k.em
                ? `em:${k.em}`
                : k.ph
                    ? `ph:${k.ph}`
                    : null;
    return id ? `${st}\u0000${id}` : null;
}
/**
 * Fold one project's queue batch into profile deltas. Only analytics-consented, non-internal
 * events with a profile key count; sensitive categories are dropped here, before any store.
 * The fingerprint (`ctx.fingerprint`) and cookieless daily hashes are never keys.
 *
 * Personalisation (docs/intent.md §4): a group whose latest stated personalisation is off
 * becomes a revoke and writes nothing; a group that never states it (server events only) may
 * update a profile that has it, never create one.
 */
export function extractIntentBatch(events, config, key, projectId) {
    const cfg = intentConfigOf(config);
    const groups = new Map();
    const withdrawals = [];
    const revoked = [];
    const optOuts = new Map();
    for (const ev of events) {
        if (ev.internal || ev.event === 'purchase_item' || ev.event.startsWith('_')) {
            continue;
        }
        const granted = analyticsGranted(ev);
        // A withdrawal receipt names the receipt it replaces (a cookieless one can't name the
        // visitor): analytics or personalisation withdrawn erases the profile it was linked to.
        const prev = ev.params.prev;
        if (ev.event === CONSENT_EVENT &&
            typeof prev === 'string' &&
            prev &&
            (!granted || ev.personalisation !== true)) {
            withdrawals.push({
                st: ev.subTenant,
                prev: prev.slice(0, 64),
                receipt: ev.id,
                consent: consentOf(ev),
            });
        }
        if (ev.intentExcluded === true) {
            // EEA, UK, Switzerland, Quebec (intentExcludedRegion, stamped at ingest): nothing
            // is built, scored or linked. Only the withdrawal above may erase an earlier profile.
            continue;
        }
        if (!granted) {
            // GPC made this device cookieless, but its hashed ids still say whose it is.
            const h = ev.hashed;
            if (ev.gpc === true && (h?.em || h?.ph)) {
                const k = `${ev.subTenant}\u0000${h.em ?? ''}\u0000${h.ph ?? ''}`;
                optOuts.set(k, {
                    st: ev.subTenant,
                    keys: { ...(h.em ? { em: h.em } : {}), ...(h.ph ? { ph: h.ph } : {}) },
                    at: Math.max(ev.ts, optOuts.get(k)?.at ?? 0),
                });
            }
            continue;
        }
        const keys = profileKeysOf(ev);
        const gk = groupKey(ev.subTenant, keys);
        if (!gk) {
            continue;
        }
        const rule = signalOf(ev.event, cfg);
        let cats = rule ? categoriesOf(ev, cfg) : [];
        if (cats.length > 0 && cats.every(c => isSensitiveCategory(c, cfg.sensitive))) {
            // Sensitive interest (decision D6): the event leaves no trace in Intent.
            continue;
        }
        cats = cats.filter(c => !isSensitiveCategory(c, cfg.sensitive));
        let acc = groups.get(gk);
        if (!acc) {
            acc = {
                delta: {
                    st: ev.subTenant,
                    keys: {},
                    create: false,
                    verified: false,
                    first: ev.ts,
                    last: ev.ts,
                    country: ev.source === 'client' ? ev.country : '',
                    device: ev.device,
                    consent: null,
                    receipts: [],
                    counts: {},
                    sinceLead: {},
                    leads: [],
                    interests: [],
                },
                byCat: new Map(),
                signals: [],
                pz: null,
                trusted: {},
                optOut: { gpcAt: 0, deniedAt: 0, grantedBefore: false },
                firstAdAt: 0,
            };
            groups.set(gk, acc);
        }
        const d = acc.delta;
        d.keys = { ...d.keys, ...keys };
        if (ev.source === 'server') {
            acc.trusted = { ...acc.trusted, ...keys };
        }
        else if (keys.v) {
            acc.trusted = { ...acc.trusted, v: keys.v };
        }
        if (statesPersonalisation(ev) && (!acc.pz || ev.ts >= acc.pz.at)) {
            acc.pz = { p: ev.personalisation === true, at: ev.ts };
        }
        if (ev.gpc === true) {
            acc.optOut.gpcAt = Math.max(acc.optOut.gpcAt, ev.ts);
        }
        if (deniesAdvertising(ev)) {
            acc.optOut.deniedAt = Math.max(acc.optOut.deniedAt, ev.ts);
        }
        else if (advertisingAllowed(ev) && (!acc.firstAdAt || ev.ts < acc.firstAdAt)) {
            acc.firstAdAt = ev.ts;
        }
        if (ev.source === 'server' && (keys.u || keys.em || keys.ph)) {
            d.verified = true;
        }
        d.first = Math.min(d.first, ev.ts);
        if (ev.ts >= d.last) {
            d.last = ev.ts;
            // A server event's country may be the backend's own location: browsers only.
            d.country = (ev.source === 'client' ? ev.country : '') || d.country;
            d.device = ev.device;
        }
        if (statesConsent(ev) && (!d.consent || ev.ts >= d.consent.at)) {
            d.consent = consentOf(ev);
        }
        if (ev.event === CONSENT_EVENT) {
            d.receipts.push(ev.id);
        }
        if (!rule) {
            continue;
        }
        const known = cats.filter(c => c !== UNKNOWN_CATEGORY);
        if (NEEDS_CATEGORY.has(rule.kind) && known.length === 0) {
            continue;
        }
        d.counts[rule.kind] = (d.counts[rule.kind] ?? 0) + 1;
        acc.signals.push({ kind: rule.kind, at: ev.ts });
        if (rule.kind === 'lead') {
            d.leads.push({ id: ev.id, label: ev.label, at: ev.ts });
        }
        for (const cat of cats) {
            const list = acc.byCat.get(cat) ?? [];
            list.push({
                kind: rule.kind,
                weight: rule.weight,
                ...(rule.sessionCap !== undefined ? { cap: rule.sessionCap } : {}),
                session: ev.session,
                at: ev.ts,
            });
            acc.byCat.set(cat, list);
        }
    }
    const profiles = [];
    for (const acc of groups.values()) {
        const d = acc.delta;
        if (acc.pz && !acc.pz.p) {
            // Analytics without personalisation: the events count in reports, nothing is
            // profiled, and a profile these keys link to (an earlier grant) is erased.
            if (Object.keys(acc.trusted).length > 0) {
                revoked.push({ st: d.st, keys: acc.trusted, at: acc.pz.at });
            }
            continue;
        }
        if (d.consent) {
            // The latest consent statement may be a server one that can't state personalisation.
            d.consent.p = acc.pz ? acc.pz.p : null;
        }
        if (acc.optOut.gpcAt || acc.optOut.deniedAt) {
            acc.optOut.grantedBefore = acc.firstAdAt > 0 && acc.firstAdAt < acc.optOut.deniedAt;
            d.optOut = acc.optOut;
        }
        d.create =
            !!acc.pz &&
                (acc.signals.length > 0 ||
                    d.receipts.length > 0 ||
                    !!(d.keys.u || d.keys.em || d.keys.ph));
        const lastLead = Math.max(0, ...d.leads.map(l => l.at));
        for (const s of acc.signals) {
            if (s.kind !== 'lead' && s.at >= lastLead) {
                d.sinceLead[s.kind] = (d.sinceLead[s.kind] ?? 0) + 1;
            }
        }
        d.leads = d.leads.sort((a, b) => b.at - a.at).slice(0, 5);
        for (const [cat, signals] of acc.byCat) {
            const hl = halfLifeDays(cat, cfg) * DAY_MS;
            const at = Math.max(...signals.map(s => s.at));
            const interest = { cat, top: gptTop(cat), hl, at, add: 0 };
            const capped = new Map();
            for (const s of signals) {
                const stage = KIND_DEFAULTS[s.kind].stage;
                if (s.kind === 'purchase') {
                    interest.purchaseAt = Math.max(interest.purchaseAt ?? 0, s.at);
                    continue;
                }
                if (stage === 'considering') {
                    interest.considerAt = Math.max(interest.considerAt ?? 0, s.at);
                }
                else if (stage === 'intent') {
                    interest.intentAt = Math.max(interest.intentAt ?? 0, s.at);
                }
                if (s.cap !== undefined) {
                    const ck = `${s.kind}\u0000${s.session}`;
                    const c = capped.get(ck) ?? {
                        kind: s.kind,
                        session: s.session,
                        points: 0,
                        cap: s.cap,
                    };
                    c.points += s.weight;
                    capped.set(ck, c);
                }
                else {
                    interest.add += decay(s.weight, s.at, at, hl);
                }
            }
            if (capped.size > 0) {
                interest.capped = [...capped.values()];
            }
            d.interests.push(interest);
        }
        profiles.push(d);
    }
    return {
        key,
        projectId,
        retentionDays: intentRetentionDays(config),
        profiles,
        withdrawals,
        revoked,
        optOuts: [...optOuts.values()],
    };
}
//# sourceMappingURL=scoring.js.map